import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeCanvas } from 'qrcode.react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const inputStyle = {
  height: 44,
  fontSize: 15,
  padding: '0 18px',
  borderRadius: 6,
  border: '1px solid #999',
  width: '100%',
  boxSizing: 'border-box'
}

const MFAModal = ({ open, enabled, onClose, onStatusChange }) => {
  const { t } = useTranslation()

  const [mfaSetup, setMfaSetup] = useState(null)
  const [mfaCode, setMfaCode] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  useEffect(() => {
    if (!open) return

    setMfaSetup(null)
    setMfaCode('')
    setMessage(null)

    const loadMfaSetup = async () => {
      if (enabled) return

      setLoading(true)

      try {
        const res = await fetchWithAuth('/auth/mfa/setup', {
          method: 'POST'
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          setMessage({
            message: data?.message || t('SAVE_ERROR'),
            type: 'error'
          })
          return
        }

        setMfaSetup(data)
      } finally {
        setLoading(false)
      }
    }

    loadMfaSetup()

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [open, enabled, onClose, t])

  const enableMfa = async () => {
    setLoading(true)
    setMessage(null)

    try {
      const res = await fetchWithAuth('/auth/mfa/enable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: mfaCode
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setMessage({
          message: data?.message || t('SAVE_ERROR'),
          type: 'error'
        })
        return
      }

      onStatusChange(true)
      setMfaSetup(null)
      setMfaCode('')

      setMessage({
        message: data?.message || t('SAVED'),
        type: 'success'
      })
	onClose()
    } finally {
      setLoading(false)
    }
  }

  const disableMfa = async () => {
    setLoading(true)
    setMessage(null)

    try {
      const res = await fetchWithAuth('/auth/mfa/disable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: mfaCode
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setMessage({
          message: data?.message || t('SAVE_ERROR'),
          type: 'error'
        })
        return
      }

      onStatusChange(false)
	onClose()
      setMfaCode('')

      setMessage({
        message: data?.message || t('SAVED'),
        type: 'success'
      })
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <>
      <Overlay open={loading} zIndex={5000} backdropOpacity={0.75} />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 4500
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '20px 30px',
            border: '2px solid #107c6b',
            borderRadius: 14,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: 390,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}
        >
          <h3 style={{ fontSize: 24, margin: 0 }}>
            {t('MFA_AUTH_APP')}
          </h3>

          {message && (
            <MessageBox
              message={message.message}
              type={message.type}
            />
          )}

          <strong>
            {enabled ? t('MFA_ENABLED') : t('MFA_DISABLED')}
          </strong>

          {mfaSetup && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10
              }}
            >
              <QRCodeCanvas
                value={mfaSetup.otpauthUrl || ''}
                size={180}
              />

              <input
                value={mfaSetup.secret || ''}
                readOnly
                style={{
                  ...inputStyle,
                  background: '#ddd'
                }}
              />
            </div>
          )}

          {(enabled || mfaSetup) && (
<input
  autoFocus
  id="mfa"
  name="otp"
  type="text"
  autoComplete="off"
  autoCorrect="off"
  autoCapitalize="off"
  spellCheck={false}
	  value={mfaCode}
 	 onChange={(e) => setMfaCode(e.target.value)}
  	onKeyDown={(e) => {
 	   if (e.key === 'Enter' && !loading) {
 	     enabled ? disableMfa() : enableMfa()
   	 }
 	 }}
 	 placeholder={t('MFA_CODE')}
 	 style={{
	    ...inputStyle,
	    background: '#fff',
 	   boxSizing: 'border-box'
	  }}
	/>
          )}

          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              marginTop: 8
            }}
          >
            <button
              style={modalPrimaryButtonStyle(
                pressedBtn,
                'mfa',
                hoverBtn === 'mfa'
              )}
              onMouseEnter={() => setHoverBtn('mfa')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('mfa')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={enabled ? disableMfa : enableMfa}
            >
              {enabled
                ? t('MFA_DISABLE')
                : t('MFA_CONFIRM_ENABLE')}
            </button>

            <button
              style={modalSecondaryButtonStyle(
                pressedBtn,
                'close-mfa',
                hoverBtn === 'close-mfa'
              )}
              onMouseEnter={() => setHoverBtn('close-mfa')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('close-mfa')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('CLOSE')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default MFAModal